/**
 * Custom hook for managing suggested questions and tasks
 *
 * Extracted from ChatPanel.tsx to reduce complexity
 */

import { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import { studioRegistry } from '../../../services/studioRegistry';
import type { MinimalDocument } from '../../../types';
import logger from '../../../utils/simpleLogger';

export interface SuggestedStudioTask {
  id: string;
  name: string;
}

const DEFAULT_STUDIO_TASKS: SuggestedStudioTask[] = [
  { id: 'resumen', name: 'Resumen Ejecutivo' },
  { id: 'timeline', name: 'Línea de Tiempo' },
  { id: 'qa', name: 'Preguntas y Respuestas' },
];

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  isStreaming?: boolean;
  markdown?: string;
  citations?: Record<string, { document_id?: string }>;
}

export interface UseSuggestedContentProps {
  messages: ChatMessage[];
  selectedMinimalDocuments: MinimalDocument[] | null;
  conversationId?: string;
  isTyping?: boolean;
  questionsEnabled: boolean;
  tasksEnabled: boolean;
}

export interface UseSuggestedContentReturn {
  // Questions
  suggestedQuestions: string[];
  isLoadingQuestions: boolean;
  showQuestions: boolean;
  questionsQuality: any;
  generationMetadata: any;
  // Tasks (studio id + display name for bubble and studio_id in send)
  suggestedTasks: SuggestedStudioTask[];
  isLoadingTasks: boolean;
  showTasks: boolean;
}

/**
 * Hook to manage suggested questions and tasks generation
 */
export function useSuggestedContent({
  messages,
  selectedMinimalDocuments,
  conversationId,
  isTyping = false,
  questionsEnabled,
  tasksEnabled,
}: UseSuggestedContentProps): UseSuggestedContentReturn {
  // Questions state
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [questionsQuality, setQuestionsQuality] = useState<any>(null);
  const [generationMetadata, setGenerationMetadata] = useState<any>(null);

  // Tasks state (id + name for studio_id in send)
  const [suggestedTasks, setSuggestedTasks] = useState<SuggestedStudioTask[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [showTasks, setShowTasks] = useState(false);

  // Generate suggested questions
  useEffect(() => {
    if (!questionsEnabled) {
      setShowQuestions(false);
      setSuggestedQuestions([]);
      setQuestionsQuality(null);
      setGenerationMetadata(null);
      return;
    }

    const hasDocuments = selectedMinimalDocuments && selectedMinimalDocuments.length > 0;
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    const isEmptyState = messages.length === 0;
    const afterAiResponse =
      lastMessage && !lastMessage.isUser && !lastMessage.isStreaming && !isTyping;

    // Extract document IDs from last message citations (for auto-selected docs
    // that may no longer be in selectedMinimalDocuments after send cleanup)
    const citationDocIds =
      afterAiResponse && lastMessage?.citations
        ? [
            ...new Set(
              Object.values(lastMessage.citations)
                .map((c) => c.document_id)
                .filter(Boolean) as string[]
            ),
          ]
        : [];
    const hasDocContext = hasDocuments || citationDocIds.length > 0;

    if (hasDocContext && !isTyping && (isEmptyState || afterAiResponse)) {
      setShowQuestions(true);

      const generateSuggestions = async () => {
        setIsLoadingQuestions(true);
        try {
          const documentIds = hasDocuments
            ? selectedMinimalDocuments!.map((doc) => doc.id)
            : citationDocIds;
          let lastAiResponse: string | undefined;

          if (afterAiResponse && lastMessage) {
            lastAiResponse = lastMessage.markdown || lastMessage.text;
          }

          const response = await apiService.getAISuggestedQuestions({
            document_ids: documentIds,
            conversation_id: conversationId,
            last_ai_response: lastAiResponse,
          });

          if (response.success) {
            setSuggestedQuestions(response.suggestions || []);
            setQuestionsQuality(response.quality || null);
            setGenerationMetadata(response.generation_metadata || null);

            if (!response.suggestions || response.suggestions.length === 0) {
              setShowQuestions(false);
            }
          } else {
            setSuggestedQuestions([]);
            setShowQuestions(false);
            setQuestionsQuality(null);
            setGenerationMetadata(null);
          }
        } catch (err: any) {
          logger.error('Error generating suggested questions:', err);
          setSuggestedQuestions([]);
          setShowQuestions(false);
          setQuestionsQuality(null);
          setGenerationMetadata(null);
        } finally {
          setIsLoadingQuestions(false);
        }
      };

      const timeoutId = setTimeout(
        () => {
          generateSuggestions();
        },
        isEmptyState ? 100 : 300
      );

      return () => clearTimeout(timeoutId);
    } else {
      setShowQuestions(false);
      setSuggestedQuestions([]);
      setQuestionsQuality(null);
      setGenerationMetadata(null);
    }
  }, [messages, isTyping, selectedMinimalDocuments, conversationId, questionsEnabled]);

  // Generate suggested tasks
  useEffect(() => {
    if (!tasksEnabled) {
      setShowTasks(false);
      setSuggestedTasks([] as SuggestedStudioTask[]);
      return;
    }

    const hasDocuments = selectedMinimalDocuments && selectedMinimalDocuments.length > 0;
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    const isEmptyState = messages.length === 0;
    const afterAiResponse =
      lastMessage && !lastMessage.isUser && !lastMessage.isStreaming && !isTyping;

    // Also show tasks when last response has citations (auto-selected docs)
    const hasCitationDocs =
      afterAiResponse && lastMessage?.citations && Object.keys(lastMessage.citations).length > 0;
    const hasDocContext = hasDocuments || !!hasCitationDocs;

    if (hasDocContext && !isTyping && (isEmptyState || afterAiResponse)) {
      setShowTasks(true);

      const generateTasks = async () => {
        setIsLoadingTasks(true);
        try {
          const studios = await studioRegistry.getStudios();
          // Only show global (built-in) studios as suggestions — exclude user-created ones
          const globalStudios = studios.filter((s) => !s.is_custom);
          const tasks: SuggestedStudioTask[] =
            globalStudios.length > 0
              ? globalStudios.map((s) => ({ id: s.id, name: s.name }))
              : DEFAULT_STUDIO_TASKS;
          setSuggestedTasks(tasks);
          setShowTasks(true);
          logger.info('[SuggestedTasks] Showing all studios in bubble', {
            count: tasks.length,
            tasks,
          });
        } catch (err: any) {
          logger.warn('getStudios failed, using default studio names', err);
          setSuggestedTasks(DEFAULT_STUDIO_TASKS);
          setShowTasks(true);
        } finally {
          setIsLoadingTasks(false);
        }
      };

      const timeoutId = setTimeout(
        () => {
          generateTasks();
        },
        isEmptyState ? 100 : 300
      );

      return () => clearTimeout(timeoutId);
    } else {
      setShowTasks(false);
      setSuggestedTasks([] as SuggestedStudioTask[]);
    }
  }, [messages, isTyping, selectedMinimalDocuments, conversationId, tasksEnabled]);

  return {
    suggestedQuestions,
    isLoadingQuestions,
    showQuestions,
    questionsQuality,
    generationMetadata,
    suggestedTasks,
    isLoadingTasks,
    showTasks,
  };
}
